import React, { useState, useEffect } from 'react';

interface EmailPreviewProps {
  templateUrl: string;
  templateName: string;
}

const EmailPreview = ({ templateUrl, templateName }: EmailPreviewProps) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const response = await fetch(templateUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch template');
        }
        const html = await response.text();
        setHtmlContent(html);
      } catch (err) {
        setError('Failed to load preview');
        console.error('Error fetching template:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [templateUrl]);

  if (loading) {
    return (
      <div className="w-full max-w-[1300px] sm:max-w-[1600px] lg:max-w-[2000px] xl:max-w-[2300px] 2xl:max-w-[2500px] mx-auto h-[500px] bg-muted border rounded-lg flex items-center justify-center">
        <div className="text-muted-foreground">Loading preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-[1300px] sm:max-w-[1600px] lg:max-w-[2000px] xl:max-w-[2300px] 2xl:max-w-[2500px] mx-auto h-[500px] bg-muted border rounded-lg flex items-center justify-center">
        <div className="text-destructive">{error}</div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1300px] sm:max-w-[1600px] lg:max-w-[2000px] xl:max-w-[2300px] 2xl:max-w-[2500px] mx-auto">
      <div className="bg-background border rounded-lg overflow-hidden shadow-sm">
        <iframe
          srcDoc={`
            <style>
              body { margin: 0; padding: 0; overflow: auto; }
              a, button, input, select, textarea, [onclick], [href] { 
                pointer-events: none !important; 
                cursor: default !important; 
                text-decoration: none !important;
              }
              * { user-select: none; }
            </style>
            ${htmlContent}
          `}
          className="w-full h-[400px] sm:h-[500px] border-0"
          title={`Preview of ${templateName}`}
          sandbox="allow-same-origin"
        />
      </div>
    </div>
  );
};

export default EmailPreview;